import React, { useState, useRef } from 'react';
import { IMAGE_SIZES, getImageType } from '../config/imageConfig';
import imageManifest from '../config/imageManifest.json';

const Image = ({
  src,
  alt,
  type,
  priority,
  className = '',
  onLoad,
  width,
  height,
  sizes,
  loading,
  fetchpriority,
  style,
  disableResponsive = false,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const triedOriginal = useRef(false);

  // Determine image type / responsive size hints
  const imageType = type || getImageType(src);
  const config = IMAGE_SIZES[imageType] || IMAGE_SIZES.default;
  const isPriority = priority !== undefined ? priority : false;
  const imageSizes = sizes || config.sizes;

  // Extract base path + extension
  const lastDot = src.lastIndexOf('.');
  const basePath = src.substring(0, lastDot);
  const originalExt = src.substring(lastDot + 1);

  // Look up the responsive variants generated by scripts/optimize-images.js
  const manifestKey = (src.startsWith('/') ? src.substring(1) : src).replace(/^images\//, '');
  const manifestEntry = imageManifest[manifestKey];

  const availableWidths = [];
  if (manifestEntry && manifestEntry.variants) {
    const widthSet = new Set();
    manifestEntry.variants.forEach((variant) => {
      const match = variant.match(/-(\d+)w\./);
      if (match) widthSet.add(parseInt(match[1], 10));
    });
    availableWidths.push(...Array.from(widthSet).sort((a, b) => a - b));
  }
  const hasVariants = availableWidths.length > 0;

  // Paths must be URI-encoded: unescaped spaces (e.g. team photo filenames)
  // are srcset syntax errors and make browsers drop the candidate entirely.
  const srcSetFor = (format) =>
    availableWidths
      .map((w) => `${encodeURI(`${basePath}-${w}w.${format || originalExt}`)} ${w}w`)
      .join(', ');

  // Never use the full-size original as the loaded source: fall back to the
  // smallest generated variant in the original format. The browser only fetches
  // this when no <source> matches (very old browsers), so it stays cheap.
  const fallbackSrc = hasVariants
    ? encodeURI(`${basePath}-${availableWidths[0]}w.${originalExt}`)
    : src;

  const imgStyle = {
    // Reserve space to avoid layout shift when width/height are known
    aspectRatio: width && height ? `${width}/${height}` : undefined,
    ...style,
  };

  const handleLoad = (e) => {
    setIsLoaded(true);
    setHasError(false);
    onLoad?.(e);
  };

  const handleError = (e) => {
    // Retry with the original asset at most once; if that also fails (or there
    // was no smaller variant to begin with), surface the error state rather
    // than refetching a missing file forever.
    if (!triedOriginal.current && fallbackSrc !== src) {
      triedOriginal.current = true;
      e.target.src = src;
    } else {
      setHasError(true);
    }
  };

  const useResponsive = hasVariants && !disableResponsive && !hasError;

  const img = (
    <img
      src={hasError ? src : fallbackSrc}
      srcSet={useResponsive ? srcSetFor() : undefined}
      sizes={useResponsive ? imageSizes : undefined}
      alt={alt}
      width={width}
      height={height}
      loading={isPriority ? 'eager' : loading || 'lazy'}
      fetchpriority={isPriority ? 'high' : fetchpriority || 'auto'}
      decoding="async"
      onLoad={handleLoad}
      onError={handleError}
      style={imgStyle}
      className={`${className} ${!isLoaded && !hasError ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
      {...props}
    />
  );

  if (!useResponsive) {
    return img;
  }

  // Modern browsers pick the first supported <source>; the <img> above is only
  // the fallback, so the heavy original format is never downloaded.
  return (
    <picture>
      <source type="image/avif" srcSet={srcSetFor('avif')} sizes={imageSizes} />
      <source type="image/webp" srcSet={srcSetFor('webp')} sizes={imageSizes} />
      {img}
    </picture>
  );
};

export default Image;
